<script setup lang="ts">
	import axios from '@/lib/axios'
	import { computed, onUnmounted, ref, useId, watch } from 'vue'

	import {
		Select,
		SelectContent,
		SelectItem,
		SelectTrigger,
		SelectValue,
	} from '@/components/ui/select'

	import useAuthUser from '@/composables/useAuthUser'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'

	import HotelLoyaltySourceBadge from './HotelLoyaltySourceBadge.vue'

	type Program = { id: number; name: string }
	type Membership = {
		id: number
		loyaltyProgramId: number
		loyaltyProgramName: string
		maskedNumber: string
		isDefault: boolean
		source: 'company' | 'traveler'
	}
	type HotelLoyalty = {
		chainId: number | null
		chainName: string | null
		programs: Program[]
		memberships: Membership[]
		personalMemberships: Membership[]
		allowClientHotelLoyalty: boolean
		loyaltyScope?: 'off' | 'private' | 'all'
	}

	const props = defineProps<{
		hotelId?: string | number
		hotelCode?: string
		companyId: number
		travelerId: number
		provider?: 'amadeus' | 'hotelbeds'
		isCompanyRate?: boolean
		modelValue?: number | null
	}>()
	const emit = defineEmits<{ 'update:modelValue': [value: number | null] }>()
	const selectId = useId()

	const { hasPermissionUser } = useHasPermissionUser()
	const { user } = useAuthUser()
	const canManage = computed(() =>
		hasPermissionUser('manage-loyaltyNumbers', 'App\\Models\\Company')
	)
	const canAddPersonal = computed(
		() =>
			Boolean(data.value?.allowClientHotelLoyalty) &&
			Number(user.value?.id) === Number(props.travelerId)
	)
	const data = ref<HotelLoyalty | null>(null)
	const loading = ref(false)
	const saving = ref(false)
	const showForm = ref(false)
	const numberOwner = ref<'company' | 'traveler'>('traveler')
	const programId = ref<number | null>(null)
	const number = ref('')
	const error = ref('')
	let requestVersion = 0

	const availablePrograms = computed(
		() =>
			data.value?.programs.filter(
				(program) =>
					!(
						numberOwner.value === 'company' ?
							data.value?.memberships
						:	data.value?.personalMemberships)?.some(
						(membership) => membership.loyaltyProgramId === program.id
					)
			) ?? []
	)
	const allMemberships = computed(() => [
		...(data.value?.personalMemberships ?? []),
		...(data.value?.memberships ?? []),
	])
	const selected = computed(() =>
		allMemberships.value.find(
			(membership) => membership.id === props.modelValue
		)
	)
	const selectedMembershipId = computed({
		get: () => props.modelValue?.toString() ?? '',
		set: (value: string) => emit('update:modelValue', Number(value) || null),
	})
	const hasPersonalSlot = computed(
		() =>
			data.value?.programs.some(
				(program) =>
					!data.value?.personalMemberships.some(
						(membership) => membership.loyaltyProgramId === program.id
					)
			) ?? false
	)
	const hasCompanySlot = computed(
		() =>
			data.value?.programs.some(
				(program) =>
					!data.value?.memberships.some(
						(membership) => membership.loyaltyProgramId === program.id
					)
			) ?? false
	)

	function openForm(owner: 'company' | 'traveler') {
		numberOwner.value = owner
		programId.value = null
		showForm.value = true
	}

	async function load() {
		const version = ++requestVersion
		if ((!props.hotelId && !props.hotelCode) || !props.companyId) {
			data.value = null
			emit('update:modelValue', null)
			return
		}

		loading.value = true
		error.value = ''
		try {
			const path =
				props.hotelId ?
					`/api/hotels/${props.hotelId}/loyalty`
				:	`/api/hotels/by-code/${encodeURIComponent(props.hotelCode!)}/loyalty`
			const response = await axios.get<HotelLoyalty>(path, {
				params: {
					companyId: props.companyId,
					travelerId: props.travelerId,
					provider: props.provider ?? 'amadeus',
					rateOwner: props.isCompanyRate === false ? 'unattributed' : 'company',
				},
				errorHandling: false,
			} as any)
			if (version !== requestVersion) return
			data.value = response.data
			if (
				response.data.loyaltyScope === 'off' ||
				((response.data.loyaltyScope ?? 'private') !== 'all' &&
					props.isCompanyRate === false)
			) {
				emit('update:modelValue', null)
				return
			}
			const memberships = [
				...(response.data.personalMemberships ?? []),
				...(response.data.memberships ?? []),
			]
			const current = memberships.find(
				(membership) => membership.id === props.modelValue
			)
			const companyDefault =
				response.data.memberships?.find((membership) => membership.isDefault) ??
				response.data.memberships?.[0]
			const chosen =
				response.data.allowClientHotelLoyalty ?
					(current ??
					response.data.personalMemberships?.find(
						(membership) => membership.isDefault
					) ??
					companyDefault ??
					memberships[0])
				:	companyDefault
			emit('update:modelValue', chosen?.id ?? null)
		} catch {
			if (version !== requestVersion) return
			data.value = null
			emit('update:modelValue', null)
			error.value =
				'No se pudieron consultar los programas de este hotel. Puedes continuar con la reserva; solicita a la agencia que confirme el registro de la cadena y su programa.'
		} finally {
			if (version === requestVersion) loading.value = false
		}
	}

	async function save() {
		if (!programId.value || !number.value.trim()) {
			error.value = 'Elige un programa y escribe el número de lealtad.'
			return
		}

		saving.value = true
		error.value = ''
		try {
			const path =
				numberOwner.value === 'company' ?
					`/api/companies/${props.companyId}/loyalty-numbers`
				:	`/api/users/${props.travelerId}/memberships`
			const response = await axios.post<{ id: number }>(
				path,
				{
					loyaltyProgramId: programId.value,
					number: number.value.trim(),
					...(numberOwner.value === 'company' ?
						{ isDefault: !data.value?.memberships.length }
					:	{}),
				},
				{ errorHandling: false } as any
			)
			showForm.value = false
			number.value = ''
			programId.value = null
			await load()
			if (data.value?.allowClientHotelLoyalty)
				emit('update:modelValue', response.data.id)
		} catch (cause: any) {
			error.value =
				cause.response?.data?.message ??
				'No se pudo guardar el número. Revisa los datos e intenta de nuevo.'
		} finally {
			saving.value = false
		}
	}

	watch(
		() => [
			props.hotelId,
			props.hotelCode,
			props.companyId,
			props.travelerId,
			props.provider,
			props.isCompanyRate,
		],
		load,
		{ immediate: true }
	)
	onUnmounted(() => {
		requestVersion++
	})
</script>

<template>
	<div class="min-w-0 space-y-2">
		<p
			v-if="loading"
			class="text-sm text-gray-600"
		>
			Consultando programas de la cadena...
		</p>
		<p
			v-if="error"
			role="alert"
			class="text-sm text-red-700"
		>
			{{ error }}
			<RouterLink
				to="/contact"
				class="font-medium underline"
			>
				Contactar a la agencia
			</RouterLink>
		</p>

		<template
			v-if="
				data &&
				!loading &&
				data.loyaltyScope !== 'off' &&
				(props.isCompanyRate !== false || data.loyaltyScope === 'all')
			"
		>
			<p class="text-sm font-semibold text-gray-800">Programa de lealtad</p>
			<p
				v-if="!data.chainId"
				class="text-sm text-gray-600"
			>
				Este hotel todavía no tiene una cadena asociada. Solicita a la agencia
				que la vincule y registre su programa de lealtad.
				<RouterLink
					to="/contact"
					class="font-medium text-[#253C70] underline"
				>
					Contactar a la agencia
				</RouterLink>
			</p>
			<p
				v-else-if="!data.programs.length"
				class="text-sm text-gray-600"
			>
				{{ data.chainName }} aún no tiene programas de lealtad registrados.
				Solicita a la agencia que dé de alta el programa de la cadena; después
				tu empresa podrá registrar su número.
				<RouterLink
					to="/contact"
					class="font-medium text-[#253C70] underline"
				>
					Contactar a la agencia
				</RouterLink>
			</p>
			<template v-else>
				<p class="text-xs text-gray-600">
					Solo aplican programas de {{ data.chainName }}.
					{{
						data.loyaltyScope === 'all' ?
							'El número personal puede enviarse en todas las tarifas cuando tu empresa lo autoriza.'
						:	'El número personal se envía únicamente en convenios privados cuando tu empresa lo autoriza.'
					}}
					El número de la empresa se muestra parcialmente.
					<span v-if="!data.allowClientHotelLoyalty">
						{{ props.isCompanyRate === false ? 'Tu empresa no ha autorizado el envío de números personales.' : 'Tu empresa eligió usar su número predeterminado sin cambios por viajero.' }}
					</span>
					Registrar un número no garantiza puntos: depende de la tarifa y del
					proveedor.
				</p>
				<div
					v-if="data.allowClientHotelLoyalty && allMemberships.length > 1"
					class="min-w-0"
				>
					<label
						:for="selectId"
						class="text-sm font-medium text-gray-700"
					>
						Número para esta reserva
					</label>
					<Select v-model="selectedMembershipId">
						<SelectTrigger
							:id="selectId"
							class="mt-1 h-auto min-h-10 w-full min-w-0 bg-white [&>span]:min-w-0 [&>span]:whitespace-normal"
						>
							<SelectValue placeholder="Selecciona un número">
								<span
									v-if="selected"
									class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"
								>
									<HotelLoyaltySourceBadge :source="selected.source" />
									<span class="min-w-0 break-words"
										>{{ selected.loyaltyProgramName }} ·
										{{ selected.maskedNumber }}</span
									>
									<span
										v-if="selected.isDefault"
										class="text-xs text-gray-600"
										>Predeterminado</span
									>
								</span>
							</SelectValue>
						</SelectTrigger>
						<SelectContent
							class="w-[var(--reka-select-trigger-width)] max-w-[calc(100vw-2rem)]"
						>
							<SelectItem
								v-for="membership in allMemberships"
								:key="membership.id"
								:value="membership.id.toString()"
								class="py-2"
							>
								<span
									class="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 whitespace-normal"
								>
									<HotelLoyaltySourceBadge :source="membership.source" />
									<span class="min-w-0 break-words"
										>{{ membership.loyaltyProgramName }} ·
										{{ membership.maskedNumber }}</span
									>
									<span
										v-if="membership.isDefault"
										class="text-xs text-gray-600"
										>Predeterminado</span
									>
								</span>
							</SelectItem>
						</SelectContent>
					</Select>
				</div>
				<div
					v-else-if="selected"
					class="rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-800"
					aria-label="Número de lealtad para esta reserva"
				>
					<span class="flex min-w-0 flex-wrap items-center gap-2">
						<HotelLoyaltySourceBadge :source="selected.source" />
						<span class="min-w-0 break-words"
							>{{ selected.loyaltyProgramName }} ·
							{{ selected.maskedNumber }}</span
						>
					</span>
				</div>
				<p
					v-else
					class="text-sm text-gray-600"
				>
					{{ props.isCompanyRate === false ? 'No hay un número personal disponible para esta cadena.' : 'Todavía no hay un número de la empresa para esta cadena.' }} Puedes continuar con la reserva.
				</p>

				<div
					v-if="!showForm"
					class="flex flex-wrap gap-3"
				>
					<button
						v-if="canAddPersonal && hasPersonalSlot"
						type="button"
						class="text-sm font-medium text-[#253C70] underline"
						@click="openForm('traveler')"
					>
						Agregar mi número
					</button>
					<button
						v-if="canManage && hasCompanySlot && props.isCompanyRate !== false"
						type="button"
						class="text-sm font-medium text-[#253C70] underline"
						@click="openForm('company')"
					>
						Agregar número de la empresa
					</button>
				</div>
				<form
					v-if="showForm"
					class="space-y-3 rounded-lg border border-gray-200 bg-white p-3"
					@submit.prevent="save"
				>
					<label class="block text-sm font-medium text-gray-700"
						>Programa
						<select
							v-model.number="programId"
							required
							class="mt-1 w-full min-w-0 rounded-lg border border-gray-300 px-3 py-2"
						>
							<option
								:value="null"
								disabled
							>
								Selecciona un programa
							</option>
							<option
								v-for="program in availablePrograms"
								:key="program.id"
								:value="program.id"
							>
								{{ program.name }}
							</option>
						</select>
					</label>
					<label class="block text-sm font-medium text-gray-700"
						>{{
							numberOwner === 'company' ?
								'Número de la empresa'
							:	'Mi número de lealtad'
						}}
						<input
							v-model="number"
							type="text"
							maxlength="64"
							autocomplete="off"
							required
							class="mt-1 w-full min-w-0 rounded-lg border border-gray-300 px-3 py-2"
							placeholder="Tal como aparece en la cuenta"
						/>
					</label>
					<p class="text-xs text-gray-600">
						{{
							numberOwner === 'company' ?
								'Se guardará en la empresa y solo se mostrará su terminación al viajero.'
							:	'Se guardará en tu perfil y se enviará en las tarifas permitidas por el proveedor cuando tu empresa lo autorice.'
						}}
					</p>
					<div class="flex flex-wrap gap-2">
						<button
							type="submit"
							:disabled="saving"
							class="rounded-lg bg-[#253C70] px-3 py-2 text-sm text-white disabled:opacity-50"
						>
							{{ saving ? 'Guardando...' : 'Guardar número' }}
						</button>
						<button
							type="button"
							class="rounded-lg border px-3 py-2 text-sm"
							@click="showForm = false"
						>
							Cancelar
						</button>
					</div>
				</form>
			</template>
		</template>
	</div>
</template>
